/**
 * Execution Timeline — Premium Linear-style: neutral cards, zinc timeline, glass + shadow.
 * Single border system (ring only), typography hierarchy, micro-interactions.
 */

import { motion, useInView, AnimatePresence, useReducedMotion } from "framer-motion";
import { useRef, useState, useEffect } from "react";
import { ScrollReveal } from "./ScrollReveal";
import { TypewriterText } from "@/components/marketing/TypewriterText";
import { cn } from "@/lib/utils";
import { Inbox, Sparkles, Rocket } from "lucide-react";

interface TimelineStep {
  number: number;
  title: string;
  description: string;
  guardrail?: string;
  expandedDescription: string;
  icon: React.ComponentType<{ className?: string }>;
}

const steps: TimelineStep[] = [
  {
    number: 1,
    title: "Leads come in",
    description: "From advertising, outbound, or other sources.",
    guardrail: "Auto-scored on arrival",
    expandedDescription:
      "Leads arrive from ads, outbound, referrals, or inbound traffic. DSOS captures them instantly and assigns an initial score so nothing starts untracked.",
    icon: Inbox,
  },
  {
    number: 2,
    title: "The system prioritizes",
    description: "Based on value, time decay, and execution risk.",
    guardrail: "If no contact in 24h → escalates to Today",
    expandedDescription:
      "Priority is computed from value, time decay, and execution risk. If no contact happens within the expected window, the lead escalates into Today.",
    icon: Sparkles,
  },
  {
    number: 3,
    title: "You execute immediately",
    description: "Call or WhatsApp. No decision needed.",
    guardrail: "Mandatory actions surface before revenue is lost",
    expandedDescription:
      "One action is presented with context — call or WhatsApp. No decision friction. Mandatory follow-ups surface before revenue becomes lost.",
    icon: Rocket,
  },
];

const enterTransition = { duration: 0.22, ease: [0, 0, 0.2, 1] as const };
const inViewThreshold = 0.2;

export function ExecutionTimeline() {
  const [expandedStep, setExpandedStep] = useState<number | null>(null);
  const [activeSteps, setActiveSteps] = useState<Set<number>>(new Set());
  const [hasAnimated, setHasAnimated] = useState<Set<number>>(new Set());
  const shouldReduceMotion = useReducedMotion();

  const step1Ref = useRef<HTMLButtonElement>(null);
  const step2Ref = useRef<HTMLButtonElement>(null);
  const step3Ref = useRef<HTMLButtonElement>(null);

  const step1InView = useInView(step1Ref, { amount: inViewThreshold, once: false });
  const step2InView = useInView(step2Ref, { amount: inViewThreshold, once: false });
  const step3InView = useInView(step3Ref, { amount: inViewThreshold, once: false });

  useEffect(() => {
    const newActive = new Set<number>();
    const newHasAnimated = new Set(hasAnimated);
    if (step1InView) {
      newActive.add(1);
      if (!hasAnimated.has(1)) newHasAnimated.add(1);
    }
    if (step2InView) {
      newActive.add(2);
      if (!hasAnimated.has(2)) newHasAnimated.add(2);
    }
    if (step3InView) {
      newActive.add(3);
      if (!hasAnimated.has(3)) newHasAnimated.add(3);
    }
    setActiveSteps(newActive);
    setHasAnimated(newHasAnimated);
  }, [step1InView, step2InView, step3InView, hasAnimated]);

  const stepRefs = [step1Ref, step2Ref, step3Ref];
  const stepInViews = [step1InView, step2InView, step3InView];

  const handleStepClick = (stepNumber: number) => {
    setExpandedStep(expandedStep === stepNumber ? null : stepNumber);
  };

  return (
    <section className="relative py-20 md:py-28 px-4 border-y border-border/30 overflow-hidden">
      {/* Subtle background halo — zinc/blue super light */}
      <div
        className="pointer-events-none absolute inset-0 -z-10"
        aria-hidden
        style={{
          background:
            "radial-gradient(70% 60% at 50% 50%, rgba(113, 113, 122, 0.06) 0%, rgba(59, 130, 246, 0.03) 40%, transparent 70%)",
        }}
      />

      <div className="container relative mx-auto max-w-3xl">
        <ScrollReveal>
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-semibold tracking-[var(--tracking-tighter)] mb-4 leading-[var(--leading-headline)] text-foreground">
              A daily execution loop
            </h2>
            <p className="text-sm md:text-base text-zinc-600 dark:text-zinc-400 max-w-xl mx-auto leading-[var(--leading-relaxed)] tracking-[var(--tracking-normal)]">
              How Decision Sales OS turns leads into immediate actions.
            </p>
          </div>
        </ScrollReveal>

        <div className="relative max-w-2xl mx-auto">
          {/* Vertical line — zinc gradient with fade top/bottom */}
          <div
            className="absolute left-5 top-0 bottom-0 w-px dark:opacity-70"
            style={{
              background:
                "linear-gradient(to bottom, transparent 0%, rgb(228 228 231) 10%, rgb(228 228 231 / 0.6) 50%, rgb(228 228 231) 90%, transparent 100%)",
            }}
            aria-hidden
          />

          <div className="space-y-6 md:space-y-8">
            {steps.map((step, index) => {
              const isActive = stepInViews[index];
              const isExpanded = expandedStep === step.number;
              const isLast = step.number === 3;
              const stepRef = stepRefs[index];
              const Icon = step.icon;

              return (
                <motion.button
                  key={step.number}
                  ref={stepRef}
                  type="button"
                  onClick={() => handleStepClick(step.number)}
                  aria-expanded={isExpanded}
                  initial={
                    shouldReduceMotion
                      ? { opacity: 1, y: 0 }
                      : { opacity: 0, y: 10 }
                  }
                  animate={
                    isActive || shouldReduceMotion
                      ? { opacity: 1, y: 0 }
                      : { opacity: 0, y: 10 }
                  }
                  transition={{
                    ...enterTransition,
                    delay: shouldReduceMotion ? 0 : index * 0.06,
                  }}
                  whileHover={shouldReduceMotion ? undefined : { y: -1 }}
                  className={cn(
                    "group relative pl-20 md:pl-24 pr-4 py-4 rounded-xl text-left w-full transition-shadow duration-200",
                    "bg-white/80 dark:bg-zinc-900/60 backdrop-blur-sm",
                    "ring-1 ring-zinc-200/80 dark:ring-zinc-700/60",
                    "shadow-[0_1px_2px_rgba(0,0,0,0.04)] dark:shadow-[0_1px_2px_rgba(0,0,0,0.2)]",
                    "hover:shadow-[0_4px_12px_rgba(0,0,0,0.06)] dark:hover:shadow-[0_4px_12px_rgba(0,0,0,0.25)]",
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 focus-visible:ring-offset-2",
                    isLast &&
                      isActive &&
                      "ring-2 ring-slate-400/25 dark:ring-white/20"
                  )}
                >
                  {/* Inner highlight — Linear-style top gradient */}
                  <div
                    className="pointer-events-none absolute inset-x-0 top-0 h-20 rounded-t-xl opacity-[0.6] dark:opacity-40"
                    style={{
                      background:
                        "linear-gradient(to bottom, rgba(255,255,255,0.5) 0%, transparent 100%)",
                    }}
                    aria-hidden
                  />
                  <div
                    className="pointer-events-none absolute inset-x-0 top-0 h-20 rounded-t-xl dark:opacity-30"
                    style={{
                      background:
                        "linear-gradient(to bottom, rgba(255,255,255,0.15) 0%, transparent 100%)",
                    }}
                    aria-hidden
                  />

                  {/* Number badge — inside card, vertically centered */}
                  <motion.div
                    className={cn(
                      "absolute left-6 top-1/2 -translate-y-1/2 z-10",
                      "flex items-center justify-center h-10 w-10 min-w-10 rounded-full",
                      "border border-zinc-200/70 dark:border-white/10",
                      "bg-white/5 dark:bg-white/[0.03]",
                      "ring-1 ring-zinc-200/50 dark:ring-white/[0.06]",
                      "shadow-[0_8px_30px_rgba(0,0,0,0.22)]"
                    )}
                    initial={
                      shouldReduceMotion
                        ? { scale: 1, opacity: 1 }
                        : { scale: 0.96, opacity: 0 }
                    }
                    animate={
                      isActive && !shouldReduceMotion
                        ? { scale: 1, opacity: 1 }
                        : { scale: 1, opacity: 1 }
                    }
                    transition={{
                      ...enterTransition,
                      delay: shouldReduceMotion ? 0 : index * 0.06 + 0.05,
                    }}
                  >
                    <span className="tabular-nums leading-none text-sm font-medium text-zinc-700 dark:text-white/80 text-foreground">
                      {step.number}
                    </span>
                  </motion.div>

                  <div className="relative z-0 space-y-2">
                    {/* Icon + Title row */}
                    <div className="flex items-center gap-2">
                      <Icon
                        className={cn(
                          "h-4 w-4 shrink-0 text-zinc-400 dark:text-zinc-500",
                          "transition-colors duration-150",
                          "group-hover:text-blue-500/80"
                        )}
                        aria-hidden
                      />
                      <h3 className="text-base md:text-lg font-semibold text-foreground tracking-[var(--tracking-tight)] leading-[var(--leading-headline)]">
                        {isActive && hasAnimated.has(step.number) && !shouldReduceMotion ? (
                          <TypewriterText
                            key={`title-${step.number}`}
                            text={step.title}
                            staggerDelay={0.015}
                          />
                        ) : (
                          step.title
                        )}
                      </h3>
                    </div>

                    <p className="text-sm md:text-base text-zinc-600 dark:text-zinc-400 leading-[1.55] tracking-[var(--tracking-normal)] pl-6">
                      {isActive && hasAnimated.has(step.number) && !shouldReduceMotion ? (
                        <TypewriterText
                          key={`desc-${step.number}`}
                          text={step.description}
                          staggerDelay={0.01}
                        />
                      ) : (
                        step.description
                      )}
                    </p>

                    {/* Guardrail pill — Linear-style: small, zinc, single ring */}
                    {step.guardrail && (
                      <motion.div
                        className="inline-flex items-center gap-1.5 pl-6 mt-2"
                        initial={
                          shouldReduceMotion
                            ? { opacity: 1, y: 0 }
                            : { opacity: 0, y: 4 }
                        }
                        animate={
                          isActive || shouldReduceMotion
                            ? { opacity: 1, y: 0 }
                            : { opacity: 0, y: 4 }
                        }
                        transition={{
                          ...enterTransition,
                          delay:
                            shouldReduceMotion ? 0 : index * 0.06 + 0.15,
                        }}
                      >
                        <span
                          className={cn(
                            "text-xs font-medium rounded-full px-2.5 py-1",
                            "bg-zinc-900/5 dark:bg-zinc-100/10 text-zinc-700 dark:text-zinc-300",
                            "ring-1 ring-black/5 dark:ring-white/10"
                          )}
                        >
                          {step.guardrail}
                        </span>
                      </motion.div>
                    )}

                    <AnimatePresence>
                      {isExpanded && (
                        <motion.div
                          initial={{ height: 0, opacity: 0, y: -4 }}
                          animate={{ height: "auto", opacity: 1, y: 0 }}
                          exit={{ height: 0, opacity: 0, y: -4 }}
                          transition={{
                            duration: 0.26,
                            ease: [0, 0, 0.2, 1],
                          }}
                          className="overflow-hidden mt-4 pt-4 pl-6 border-t border-zinc-200/60 dark:border-zinc-700/50"
                        >
                          <p className="text-sm text-zinc-600 dark:text-zinc-400 leading-relaxed">
                            {step.expandedDescription}
                          </p>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </motion.button>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
